/**
 * 🧪 MOCK DASHBOARD DATA
 * ════════════════════════════════════════════════════════════════════════
 * 
 * Data de ejemplo para testear el sistema de presentación ejecutiva.
 * Simula un dashboard de time_tracking procesado por ExecutivePresentationEngine.
 */

import type { Dashboard } from '../types/dashboard';

export const mockTimeTrackingDashboard: Dashboard = {
  id: 'mock-1',
  title: 'Control de Horas - Diciembre 2024',
  description: 'Dashboard ejecutivo de seguimiento de horas trabajadas por proyecto',
  intentType: 'time_tracking',
  
  heroInsight: {
    metricName: 'Horas Facturables',
    value: 2847,
    confidence: 'high',
    generatedBy: 'ai',
    period: 'Diciembre 2024',
    scope: 'Todos los proyectos',
    context: '85% de utilización'
  },
  
  widgets: [
    // 1️⃣ PRIMARY_INSIGHT (position 1, full width)
    {
      id: 'w1',
      type: 'metric',
      role: 'PRIMARY_INSIGHT',
      title: 'Horas Facturables Totales',
      data: [{ valor: 2847 }],
      layout: { x: 0, y: 0, w: 12, h: 5 },
      metadata: { format: 'number', decimals: 0 }
    },
    
    // 2️⃣ DRIVER - Distribución por proyecto
    {
      id: 'w2',
      type: 'bar',
      role: 'DRIVER',
      title: 'Horas por Proyecto',
      data: [
        { proyecto: 'Proyecto A', valor: 856 },
        { proyecto: 'Proyecto B', valor: 723 },
        { proyecto: 'Proyecto C', valor: 612 },
        { proyecto: 'Proyecto D', valor: 456 },
        { proyecto: 'Proyecto E', valor: 200 }
      ],
      layout: { x: 0, y: 5, w: 6, h: 4 },
      metadata: { format: 'number' }
    },
    
    // 3️⃣ DRIVER - Distribución por equipo
    {
      id: 'w3',
      type: 'bar',
      role: 'DRIVER',
      title: 'Horas por Equipo',
      data: [
        { equipo: 'Desarrollo', valor: 1234 },
        { equipo: 'Diseño', valor: 678 },
        { equipo: 'QA', valor: 456 },
        { equipo: 'DevOps', valor: 345 },
        { equipo: 'PM', valor: 134 }
      ],
      layout: { x: 6, y: 5, w: 6, h: 4 },
      metadata: { format: 'number' }
    },
    
    // 4️⃣ TREND - Evolución temporal
    {
      id: 'w4',
      type: 'line',
      role: 'TREND',
      title: 'Evolución de Horas Facturables (últimos 30 días)',
      data: [
        { fecha: '01/12', valor: 89 },
        { fecha: '02/12', valor: 95 },
        { fecha: '03/12', valor: 92 },
        { fecha: '04/12', valor: 88 },
        { fecha: '05/12', valor: 101 },
        { fecha: '06/12', valor: 97 },
        { fecha: '07/12', valor: 0 },
        { fecha: '08/12', valor: 0 },
        { fecha: '09/12', valor: 103 },
        { fecha: '10/12', valor: 99 },
        { fecha: '11/12', valor: 96 },
        { fecha: '12/12', valor: 104 },
        { fecha: '13/12', valor: 98 },
        { fecha: '14/12', valor: 0 },
        { fecha: '15/12', valor: 0 },
        { fecha: '16/12', valor: 102 },
        { fecha: '17/12', valor: 97 },
        { fecha: '18/12', valor: 95 },
        { fecha: '19/12', valor: 100 },
        { fecha: '20/12', valor: 93 }
      ],
      layout: { x: 0, y: 9, w: 12, h: 4 },
      metadata: {}
    },
    
    // 5️⃣ DETAIL - Tabla detallada
    {
      id: 'w5',
      type: 'table',
      role: 'DETAIL',
      title: 'Detalle por Empleado',
      data: [
        { empleado: 'Juan Pérez', proyecto: 'Proyecto A', horas: 168, facturables: 152 },
        { empleado: 'María García', proyecto: 'Proyecto B', horas: 165, facturables: 148 },
        { empleado: 'Carlos López', proyecto: 'Proyecto A', horas: 172, facturables: 156 },
        { empleado: 'Ana Martínez', proyecto: 'Proyecto C', horas: 160, facturables: 144 },
        { empleado: 'Luis Rodríguez', proyecto: 'Proyecto D', horas: 158, facturables: 142 },
        { empleado: 'Laura Sánchez', proyecto: 'Proyecto B', horas: 163, facturables: 147 },
        { empleado: 'Pedro Gómez', proyecto: 'Proyecto E', horas: 155, facturables: 140 },
        { empleado: 'Sofía Torres', proyecto: 'Proyecto A', horas: 170, facturables: 154 },
        { empleado: 'Diego Ruiz', proyecto: 'Proyecto C', horas: 162, facturables: 146 },
        { empleado: 'Carmen Díaz', proyecto: 'Proyecto D', horas: 159, facturables: 143 }
      ],
      layout: { x: 0, y: 13, w: 12, h: 6 },
      metadata: {}
    }
  ],
  
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

export const mockSalesDashboard: Dashboard = {
  id: 'mock-2',
  title: 'Ventas - Q4 2024',
  description: 'Dashboard ejecutivo de ventas del último trimestre',
  intentType: 'ventas',
  
  heroInsight: {
    metricName: 'Revenue Total',
    value: 1250000,
    confidence: 'high',
    generatedBy: 'ai',
    period: 'Q4 2024',
    scope: 'Todas las regiones'
  },
  
  widgets: [
    // PRIMARY_INSIGHT
    {
      id: 'w1',
      type: 'metric',
      role: 'PRIMARY_INSIGHT',
      title: 'Revenue Total Q4',
      data: [{ valor: 1250000 }],
      layout: { x: 0, y: 0, w: 12, h: 6 },
      metadata: { format: 'currency', decimals: 0 }
    },
    
    // DRIVER - Por región
    {
      id: 'w2',
      type: 'bar',
      role: 'DRIVER',
      title: 'Ventas por Región',
      data: [
        { region: 'CABA', valor: 450000 },
        { region: 'GBA', valor: 380000 },
        { region: 'Interior', valor: 290000 },
        { region: 'Exterior', valor: 130000 }
      ],
      layout: { x: 0, y: 6, w: 6, h: 4 },
      metadata: { format: 'currency' }
    },
    
    // DRIVER - Por producto
    {
      id: 'w3',
      type: 'bar',
      role: 'DRIVER',
      title: 'Ventas por Producto',
      data: [
        { producto: 'Producto A', valor: 520000 },
        { producto: 'Producto B', valor: 380000 },
        { producto: 'Producto C', valor: 230000 },
        { producto: 'Producto D', valor: 120000 }
      ],
      layout: { x: 6, y: 6, w: 6, h: 4 },
      metadata: { format: 'currency' }
    },
    
    // TREND
    {
      id: 'w4',
      type: 'line',
      role: 'TREND',
      title: 'Evolución Mensual',
      data: [
        { mes: 'Octubre', valor: 385000 },
        { mes: 'Noviembre', valor: 425000 },
        { mes: 'Diciembre', valor: 440000 }
      ],
      layout: { x: 0, y: 10, w: 12, h: 4 },
      metadata: { format: 'currency' }
    }
  ],
  
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};
